import type { ObjectData } from "@novorender/data-js-api";
import { getPublicId } from "novolib/utils";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { selectMainObject } from "features/render";
import { useUom } from "features/uom/hooks/useUom";
import { extractPropertyValueAndUnitType } from "features/uom/utils";
import { selectIsOnline, selectIsProcoreDesign } from "slices/explorer";
import { ObjectAccessForbiddenError } from "utils/appObjectDb";
import {
    getDisplayProperties,
    getFilePathFromObjectPath,
    getPropertyDisplayName,
    getTopmostFileNameFromPath,
} from "utils/objectData";

const MAX_PROPERTY_COUNT = 200;

export type PropertiesObject = {
    type: ObjectData["type"];
    id: string;
    /**
     * Name of the file containing this object, e.g. in a federated model where each
     * top-level node under the root is a separate file. `undefined` when the object's
     * path has no recognizable file node (e.g. a single, non-federated model).
     */
    fileName?: string;
    base: [string, string][];
    grouped: Record<string, { name: string; properties: [string, string][] }>;
    parent?: PropertiesObject;
    parentObjectData?: ObjectData;
};

export interface UseObjectDataOptions {
    maxProperties?: number;
    includeParent?: boolean;
}

export interface UseObjectDataResult {
    object: PropertiesObject | null;
    propertyLimitMessage: string;
    hasAccess: boolean;
    isLoading: boolean;
}

/**
 * Data-only hook for fetching and processing object data.
 * Handles UOM conversion, property grouping, and parent object loading.
 */
export function useObjectData(options: UseObjectDataOptions = {}): UseObjectDataResult {
    const { maxProperties = MAX_PROPERTY_COUNT, includeParent = false } = options;

    const { t } = useTranslation();
    const uom = useUom();
    const {
        state: { dataAccess },
    } = useExplorerGlobals(true);
    const mainObject = useAppSelector(selectMainObject);

    const [objectRaw, setObject] = useState<PropertiesObject>();
    const [hasAccess, setHasAccess] = useState(true);
    const [propertyLimitMessage, setPropertyLimitMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [currentMainObjectId, setCurrentMainObjectId] = useState<number | undefined>(undefined);
    const isOnline = useAppSelector(selectIsOnline);
    const isProcoreDesign = useAppSelector(selectIsProcoreDesign);
    const prevIsOnlineRef = useRef(isOnline);

    const object = useMemo(() => {
        if (!objectRaw) {
            return objectRaw;
        }

        const grouped = structuredClone(objectRaw.grouped);
        for (const { properties } of Object.values(grouped)) {
            for (const property of properties) {
                const unit = extractPropertyValueAndUnitType(property[1]);
                if (unit) {
                    property[1] = uom.format(uom.convertToUi(unit.value, unit.unitType), unit.unitType);
                }
            }
        }

        return {
            ...objectRaw,
            grouped,
        };
    }, [objectRaw, uom]);

    useEffect(() => {
        if (mainObject === undefined) {
            setObject(undefined);
            setCurrentMainObjectId(undefined);
            setIsLoading(false);
        } else if (mainObject !== currentMainObjectId || (!prevIsOnlineRef.current && isOnline)) {
            setIsLoading(true);
            setCurrentMainObjectId(mainObject);
            getObjectData(mainObject);
        }
        prevIsOnlineRef.current = isOnline;

        async function getObjectData(id: number) {
            setPropertyLimitMessage("");
            setHasAccess(true);

            try {
                const objectData =
                    !isOnline && !dataAccess.isDataAvailableOffline() ? undefined : await dataAccess.getObject(id);

                if (!objectData) {
                    setObject(undefined);
                    setIsLoading(false);
                    return;
                }

                const parent = includeParent && isOnline ? await dataAccess.getParent(objectData) : undefined;

                const { propertiesObject: mainPropertiesObject, filteredCount } = await createPropertiesObject(
                    objectData,
                    isProcoreDesign,
                    maxProperties,
                );

                let newPropertyLimitMessage = "";
                if (filteredCount > maxProperties) {
                    newPropertyLimitMessage = t("propertyLimitMessage1", {
                        length: filteredCount,
                        limit: maxProperties,
                    });
                }

                if (parent) {
                    // Main object is showing either filteredCount (if under limit) or maxProperties (if limited)
                    const mainPropertyCount = Math.min(filteredCount, maxProperties);

                    // Limit parent to remaining space
                    const remainingSpace = Math.max(0, maxProperties - mainPropertyCount);
                    const { propertiesObject: parentPropertiesObject, filteredCount: parentFilteredCount } =
                        await createPropertiesObject(parent, isProcoreDesign, remainingSpace);

                    // Update message if parent was limited
                    if (!newPropertyLimitMessage && parentFilteredCount > remainingSpace) {
                        newPropertyLimitMessage = t("propertyLimitMessage2", { limit: maxProperties });
                    }

                    mainPropertiesObject.parent = parentPropertiesObject;
                    mainPropertiesObject.parentObjectData = parent;
                }

                setObject(mainPropertiesObject);

                setPropertyLimitMessage(newPropertyLimitMessage);
                setIsLoading(false);
            } catch (ex) {
                setObject(undefined);
                setIsLoading(false);
                if (ex instanceof ObjectAccessForbiddenError) {
                    setHasAccess(false);
                }
            }
        }
    }, [mainObject, dataAccess, currentMainObjectId, maxProperties, includeParent, t, isOnline, isProcoreDesign]);

    return {
        object: object || null,
        propertyLimitMessage,
        hasAccess,
        isLoading,
    };
}

async function createPropertiesObject(
    object: ObjectData,
    isProcoreDesign: boolean,
    maxProperties?: number,
): Promise<{ propertiesObject: PropertiesObject; filteredCount: number }> {
    // 1. Extract public ID from UNFILTERED data first (before filtering removes "Procore/InstanceId")
    const publicId = getPublicId(object) || `${object.id}`;

    // 2. Filter properties for display (removes Procore/* internal metadata and,
    //    in the Procore Design, applies IFC group adjustments for objects in IFC files)
    const displayProperties = getDisplayProperties(object, isProcoreDesign);

    const filteredCount = displayProperties.length;

    // 3. Limit properties if needed (restores original behavior)
    const limitedProperties =
        maxProperties !== undefined ? displayProperties.slice(0, maxProperties) : displayProperties;

    // 4. Transform filtered properties into grouped structure for UI display
    const propertiesObject = limitedProperties.reduce(
        (result, [property, value]) => {
            const path = property.split("/");

            if (path.length === 1) {
                return { ...result, base: [...result.base, [property, value] as [string, string]] };
            }

            const groupName = path.slice(0, path.length - 1).join("/");
            const propertyName = path[path.length - 1];

            if (result.grouped[groupName]) {
                return {
                    ...result,
                    grouped: {
                        ...result.grouped,
                        [groupName]: {
                            ...result.grouped[groupName],
                            properties: [
                                ...result.grouped[groupName].properties,
                                [propertyName, value] as [string, string],
                            ],
                        },
                    },
                };
            }

            return {
                ...result,
                grouped: {
                    ...result.grouped,
                    [groupName]: {
                        name: getPropertyDisplayName(groupName),
                        properties: [[propertyName, value] as [string, string]],
                    },
                },
            };
        },
        {
            type: object.type,
            id: publicId,
            fileName: getTopmostFileNameFromPath(object.path) ?? undefined,
            base: [
                ["Name", object.name],
                ["Path", getFilePathFromObjectPath(object.path)],
                ["Description", object.description],
            ],
            grouped: {},
        } as PropertiesObject,
    );

    return { propertiesObject, filteredCount };
}
